/**
 * ============================================================================
 * PRODUCT CARD COMPONENT
 * ============================================================================
 * 
 * Enhanced product card with hover effects, quick actions, and interactions.
 * 
 * ============================================================================
 */



class ProductCard {
  constructor(element) {
    this.element = element;
    this.productId = data(element, 'product-id');
    this.variantId = data(element, 'variant-id');
    this.isLoading = false;
    
    this.init();
  }
  
  /**
   * Initialize product card
   */
  init() {
    this.bindEvents();
    this.setupQuickActions();
    console.log(`🛍️ Product Card initialized for product ${this.productId}`);
  }
  
  /**
   * Bind events
   */
  bindEvents() {
    // Quick add to cart
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-quick-add]')) {
        e.preventDefault();
        this.handleQuickAdd();
      }
    });
    
    // Quick view
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-quick-view]')) {
        e.preventDefault();
        this.handleQuickView();
      }
    });
    
    // Wishlist toggle
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-wishlist-toggle]')) {
        e.preventDefault();
        this.handleWishlistToggle();
      }
    });
    
    // Compare toggle
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-compare-toggle]')) {
        e.preventDefault();
        this.handleCompareToggle();
      }
    });
    
    // Variant selection
    on(this.element, 'change', (e) => {
      if (e.target.matches('[data-variant-select]')) {
        this.handleVariantChange(e.target);
      }
    });
    
    // Image hover effects
    this.setupImageHover();
  }
  
  /**
   * Setup quick actions
   */
  setupQuickActions() {
    const quickActions = $('.product-card__quick-actions', this.element);
    if (quickActions) {
      // Show/hide on hover
      this.element.addEventListener('mouseenter', () => {
        addClass(quickActions, 'show');
      });
      
      this.element.addEventListener('mouseleave', () => {
        removeClass(quickActions, 'show');
      });
    }
  }
  
  /**
   * Setup image hover effects
   */
  setupImageHover() {
    const images = $$('.product-card__image img', this.element);
    if (images.length > 1) {
      const primaryImage = images[0];
      const hoverImage = images[1];
      
      this.element.addEventListener('mouseenter', () => {
        addClass(primaryImage, 'fade-out');
        addClass(hoverImage, 'fade-in');
      });
      
      this.element.addEventListener('mouseleave', () => {
        removeClass(primaryImage, 'fade-out');
        removeClass(hoverImage, 'fade-in');
      });
    }
  }
  
  /**
   * Handle quick add to cart
   */
  async handleQuickAdd() {
    if (this.isLoading) return;
    
    this.setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', 1);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        this.showSuccess('Added to cart!');
        
        // Emit cart update event
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: { product: result, productId: this.productId }
        }));
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Quick add error:', error);
      this.showError('Failed to add to cart');
    } finally {
      this.setLoading(false);
    }
  }
  
  /**
   * Handle quick view
   */
  handleQuickView() {
    // Emit quick view event
    document.dispatchEvent(new CustomEvent('product:quickview', {
      detail: { productId: this.productId, element: this.element }
    }));
  }
  
  /**
   * Handle wishlist toggle
   */
  handleWishlistToggle() {
    const button = $('[data-wishlist-toggle]', this.element);
    const isInWishlist = hasClass(button, 'active');
    
    if (isInWishlist) {
      removeClass(button, 'active');
      this.showSuccess('Removed from wishlist');
    } else {
      addClass(button, 'active');
      this.showSuccess('Added to wishlist');
    }
    
    // Emit wishlist event
    document.dispatchEvent(new CustomEvent('wishlist:toggle', {
      detail: { 
        productId: this.productId, 
        action: isInWishlist ? 'remove' : 'add',
        element: this.element 
      }
    }));
  }
  
  /**
   * Handle compare toggle
   */
  handleCompareToggle() {
    const button = $('[data-compare-toggle]', this.element);
    const isInCompare = hasClass(button, 'active');
    
    if (isInCompare) {
      removeClass(button, 'active');
      this.showSuccess('Removed from compare');
    } else {
      addClass(button, 'active');
      this.showSuccess('Added to compare');
    }
    
    // Emit compare event
    document.dispatchEvent(new CustomEvent('compare:toggle', {
      detail: { 
        productId: this.productId, 
        action: isInCompare ? 'remove' : 'add',
        element: this.element 
      }
    }));
  }
  
  /**
   * Handle variant change
   * @param {Element} select - Variant select element
   */
  async handleVariantChange(select) {
    const newVariantId = select.value;
    this.variantId = newVariantId;
    
    // Update product data
    await this.updateProductData(newVariantId);
    
    // Emit variant change event
    document.dispatchEvent(new CustomEvent('product:variant:change', {
      detail: { 
        productId: this.productId, 
        variantId: newVariantId,
        element: this.element 
      }
    }));
  }
  
  /**
   * Update product data for new variant
   * @param {string} variantId - Variant ID
   */
  async updateProductData(variantId) {
    try {
      const response = await fetch(`/products/${this.productId}.js`);
      const product = await response.json();
      const variant = product.variants.find(v => v.id == variantId);
      
      if (variant) {
        // Update price
        const priceElement = $('.product-card__price', this.element);
        if (priceElement) {
          priceElement.textContent = this.formatMoney(variant.price);
        }
        
        // Update availability
        const availabilityElement = $('.product-card__availability', this.element);
        if (availabilityElement) {
          if (variant.available) {
            removeClass(availabilityElement, 'unavailable');
            addClass(availabilityElement, 'available');
            availabilityElement.textContent = 'In stock';
          } else {
            removeClass(availabilityElement, 'available');
            addClass(availabilityElement, 'unavailable');
            availabilityElement.textContent = 'Out of stock';
          }
        }
        
        // Update quick add button state
        const quickAddButton = $('[data-quick-add]', this.element);
        if (quickAddButton) {
          quickAddButton.disabled = !variant.available;
        }
      }
    } catch (error) {
      console.error('Failed to update product data:', error);
    }
  }
  
  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    this.isLoading = loading;
    
    if (loading) {
      addClass(this.element, 'loading');
    } else {
      removeClass(this.element, 'loading');
    }
  }
  
  /**
   * Show success message
   * @param {string} message - Success message
   */
  showSuccess(message) {
    this.showMessage(message, 'success');
  }
  
  /**
   * Show error message
   * @param {string} message - Error message
   */
  showError(message) {
    this.showMessage(message, 'error');
  }
  
  /**
   * Show message
   * @param {string} message - Message text
   * @param {string} type - Message type
   */
  showMessage(message, type = 'info') {
    // Create or update message element
    let messageElement = $('.product-card__message', this.element);
    if (!messageElement) {
      messageElement = document.createElement('div');
      messageElement.className = 'product-card__message';
      this.element.appendChild(messageElement);
    }
    
    messageElement.textContent = message;
    messageElement.className = `product-card__message ${type}`;
    addClass(messageElement, 'show');
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      removeClass(messageElement, 'show');
    }, 3000);
  }
  
  /**
   * Format money
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money
   */
  formatMoney(cents) {
    return (cents / 100).toFixed(2);
  }
  
  /**
   * Destroy product card component
   */
  destroy() {
    // Remove event listeners and clean up
    this.element.removeEventListener('mouseenter', this.handleMouseEnter);
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
  }
}
